import type {
	ListBuilderColumn,
	ListBuilderConfiguration,
	MobileCardConfiguration,
} from '../types';

/**
 * Shared quote columns definition
 */
const createQuoteColumns = (): Array<ListBuilderColumn> => [
	{
		id: 'quote-info',
		fields: [
			{
				id: 'quote-number',
				link: {
					type: 'object',
					enabled: true,
					objectTable: 'order',
				},
				dataConfig: {
					type: 'field',
					field: {
						fieldName: '_id',
						objectTable: 'order',
					},
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'theme',
						themeColor: 'textPrimary',
					},
				},
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h1', text: 'Quote #' }],
		sortable: true,
		alignment: 'left',
		verticalAlignment: 'top',
	},
	{
		id: 'client-info',
		fields: [
			{
				id: 'client-name',
				link: {
					type: 'object',
					enabled: true,
					idField: '_fk_client__id',
					objectTable: 'client',
				},
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'client_name',
						objectTable: 'order',
					},
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'static',
						staticColor: { text: '#3b82f6' },
					},
				},
				displayStyle: 'text',
			},
			{
				id: 'line-break-1',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: {
					size: 'md',
					colorRule: { type: 'default' },
				},
				displayStyle: 'line_break',
			},
			{
				id: 'client-address',
				dataConfig: {
					type: 'template',
					template: '{clientAddress1} {clientAddress2}',
					templateFields: [],
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'theme',
						themeColor: 'textSecondary',
					},
				},
				displayStyle: 'text',
			},
			{
				id: 'line-break-2',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: {
					size: 'md',
					colorRule: { type: 'default' },
				},
				displayStyle: 'line_break',
			},
			{
				id: 'client-city-state',
				dataConfig: {
					type: 'template',
					template: '{clientCity}, {clientState} {clientZip}',
					templateFields: [],
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'theme',
						themeColor: 'textSecondary',
					},
				},
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h2', text: 'Client' }],
		minWidth: 180,
		sortable: true,
		alignment: 'left',
		verticalAlignment: 'top',
	},
	{
		id: 'date-info',
		fields: [
			{
				id: 'created-date',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'createdDate',
						objectTable: 'order',
					},
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'theme',
						themeColor: 'textPrimary',
					},
					// datetime-short ("6/2/26 10:45 AM") — the medium format
					// ("Jun 2, 2026 10:45 AM") wrapped awkwardly in the mobile
					// card's right column (PD-14059 review), and short matches
					// the orders/invoices date columns on desktop.
					formatOptions: { dateFormat: 'datetime-short' },
				},
				displayStyle: 'date',
			},
			{
				id: 'date-line-break',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: {
					size: 'md',
					colorRule: { type: 'default' },
				},
				displayStyle: 'line_break',
			},
			{
				id: 'closed-date',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'expirationDate',
						objectTable: 'order',
					},
				},
				styleConfig: {
					formatOptions: { dateFormat: 'short' },
					size: 'sm',
					colorRule: {
						type: 'theme',
						themeColor: 'textSecondary',
					},
				},
				displayStyle: 'date',
			},
		],
		headers: [
			{ id: 'h3', text: 'Created' },
			{ id: 'h4', text: 'Closed' },
		],
		sortable: true,
		alignment: 'left',
		verticalAlignment: 'top',
	},
	{
		id: 'info-column',
		fields: [
			{
				id: 'needs-review-badge',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'status',
						objectTable: 'order',
					},
				},
				styleConfig: {
					size: 'xs',
					colorRule: {
						type: 'value-mapping',
						valueMapping: [
							{
								value: 'Quote - Needs Review',
								color: { bg: '#fbbf24', text: '#451a03' },
							},
						],
					},
					valueMapping: [
						{
							color: { bg: '#fbbf24', text: '#451a03' },
							value: 'Quote - Needs Review',
							displayText: 'Needs Review',
						},
						{
							value: '13',
							displayText: '',
						},
					],
				},
				displayStyle: 'badge',
			},
		],
		headers: [{ id: 'h9', text: 'Info' }],
		sortable: false,
		alignment: 'center',
		verticalAlignment: 'middle',
	},
	{
		id: 'sales-rep-info',
		fields: [
			{
				id: 'sales-rep-name',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'sales_rep_name',
						objectTable: 'order',
					},
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'theme',
						themeColor: 'textPrimary',
					},
				},
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h4', text: 'Client Sales Rep' }],
		sortable: true,
		alignment: 'left',
		verticalAlignment: 'top',
	},
	{
		id: 'lines-info',
		fields: [
			{
				id: 'number-of-lines',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'numberOfLines',
						objectTable: 'order',
					},
					defaultValue: '0',
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'theme',
						themeColor: 'textPrimary',
					},
				},
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h5', text: 'Lines' }],
		sortable: true,
		alignment: 'center',
		verticalAlignment: 'top',
	},
	{
		id: 'total-info',
		fields: [
			{
				id: 'subtotal-amount',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'subtotal',
						objectTable: 'order',
					},
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'theme',
						themeColor: 'textPrimary',
					},
				},
				displayStyle: 'currency',
			},
			{
				id: 'line-break',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: {
					size: 'md',
					colorRule: { type: 'default' },
				},
				displayStyle: 'line_break',
			},
			{
				id: 'total-amount',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'total',
						objectTable: 'order',
					},
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'theme',
						themeColor: 'textSecondary',
					},
				},
				displayStyle: 'currency',
			},
		],
		headers: [
			{ id: 'h6', text: 'Subtotal' },
			{ id: 'h7', text: 'Total' },
		],
		sortable: true,
		alignment: 'right',
		verticalAlignment: 'top',
	},
	{
		id: 'actions-info',
		fields: [
			{
				id: 'actions-dropdown',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: {
					size: 'md',
					colorRule: { type: 'default' },
				},
				displayStyle: 'actions_dropdown',
				dropdownActions: [
					{
						id: 'close-action',
						label: 'Close',
						iconName: 'ban',
						callbackName: 'closeQuote',
						variant: 'secondary',
						visibility: 'visible',
						requiredPermission: 'quotes:update',
						visibilityCallback: 'shouldShowCloseButton',
					},
					{
						id: 'delete-action',
						label: 'Delete',
						iconName: 'trash',
						callbackName: 'deleteQuote',
						variant: 'danger',
						visibility: 'visible',
						requiredPermission: 'quotes:delete',
					},
				],
			},
		],
		headers: [{ id: 'h8', text: '' }],
		sortable: false,
		alignment: 'center',
		verticalAlignment: 'middle',
		width: 100,
	},
];

/**
 * Shared required fields
 */
const QUOTE_REQUIRED_FIELDS = [
	'order._id',
	'order.createdDate',
	'order.expirationDate',
	'order.status',
	'order.fkClientId',
	'order.numberOfLines',
	'order.subtotal',
	'order.total',
	'client.name',
	'client.address1',
	'client.address2',
	'client.city',
	'client.state',
	'client.zip',
	'clientUser.firstName',
	'clientUser.lastName',
];

/**
 * Zones mobile card for quote lists (PD-14059). Two-column body per the
 * design review: the client block (name + wrapped street / city-state-zip
 * lines) sits wide-left while Created · Rep · Lines stack right-aligned
 * beside it. Quote total is the hero; Subtotal is mobile-noise next to it.
 * The Closed date and the Needs Review badge stay configured — per-row
 * empty dropping removes them when blank (the audit's orphan-dash rows).
 */
const QUOTE_MOBILE_CARD_CONFIG: MobileCardConfiguration = {
	cardLayout: 'zones',
	primaryFields: ['quote-info'],
	heroField: 'total-info.total-amount',
	wideFields: ['client-info'],
	hiddenFields: ['total-info.subtotal-amount'],
	// "Client Sales Rep" eats the right column before the name starts.
	labelOverrides: { 'sales-rep-info.sales-rep-name': 'Rep' },
};

/**
 * Quote list configuration
 */
export const QUOTE_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'open-quotes-list',
			cacheTTL: 600000,
			name: 'Open Quotes',
			type: 'default',
			active: true,
			columns: createQuoteColumns(),
			mobileCardConfig: QUOTE_MOBILE_CARD_CONFIG,
			description: 'Open quotes that are still active and can be converted',
			defaultSortId: 'date-desc',
			requiredFields: QUOTE_REQUIRED_FIELDS,
			sortOrder: 0,
			filterRules: {
				quoteSalesRep: {
					defaultValue: [],
					additionalValues: [
						{
							values: [{ id: 'ME', label: 'Me' }],
							applyIf: {
								type: 'group',
								joiner: 'AND',
								negated: false,
								children: [
									{ type: 'rule', action: 'clients:view:self', negated: false },
								],
							},
						},
						{
							values: [{ id: 'null', label: 'Unassigned' }],
							applyIf: {
								type: 'group',
								joiner: 'AND',
								negated: false,
								children: [
									{
										type: 'rule',
										action: 'clients:view:unassigned',
										negated: false,
									},
								],
							},
						},
					],
					applyDefaultIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'quotes:view:any', negated: true },
						],
					},
					boundedIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'quotes:view:any', negated: true },
						],
					},
				},
				quoteStatus: {
					defaultValue: [{ id: 'open', label: 'Open' }],
				},
			},
		},
		{
			id: 'needs-review-quotes-list',
			cacheTTL: 600000,
			name: 'Needs Review',
			type: 'default',
			active: true,
			columns: createQuoteColumns(),
			mobileCardConfig: QUOTE_MOBILE_CARD_CONFIG,
			description: 'Quotes that require review before processing',
			defaultSortId: 'date-desc',
			requiredFields: QUOTE_REQUIRED_FIELDS,
			sortOrder: 1,
			filterRules: {
				quoteSalesRep: {
					defaultValue: [],
					additionalValues: [
						{
							values: [{ id: 'ME', label: 'Me' }],
							applyIf: {
								type: 'group',
								joiner: 'AND',
								negated: false,
								children: [
									{ type: 'rule', action: 'clients:view:self', negated: false },
								],
							},
						},
						{
							values: [{ id: 'null', label: 'Unassigned' }],
							applyIf: {
								type: 'group',
								joiner: 'AND',
								negated: false,
								children: [
									{
										type: 'rule',
										action: 'clients:view:unassigned',
										negated: false,
									},
								],
							},
						},
					],
					applyDefaultIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'quotes:view:any', negated: true },
						],
					},
					boundedIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'quotes:view:any', negated: true },
						],
					},
				},
				quoteStatus: {
					defaultValue: [{ id: 'needsReview', label: 'Needs Review' }],
				},
			},
		},
		{
			id: 'default-quotes-all',
			cacheTTL: 600000,
			name: 'All Quotes',
			type: 'default',
			active: true,
			columns: createQuoteColumns(),
			mobileCardConfig: QUOTE_MOBILE_CARD_CONFIG,
			description: 'Complete list of all quotes',
			defaultSortId: 'date-desc',
			requiredFields: QUOTE_REQUIRED_FIELDS,
			sortOrder: 2,
			filterRules: {
				quoteSalesRep: {
					defaultValue: [],
					additionalValues: [
						{
							values: [{ id: 'ME', label: 'Me' }],
							applyIf: {
								type: 'group',
								joiner: 'AND',
								negated: false,
								children: [
									{ type: 'rule', action: 'clients:view:self', negated: false },
								],
							},
						},
						{
							values: [{ id: 'null', label: 'Unassigned' }],
							applyIf: {
								type: 'group',
								joiner: 'AND',
								negated: false,
								children: [
									{
										type: 'rule',
										action: 'clients:view:unassigned',
										negated: false,
									},
								],
							},
						},
					],
					applyDefaultIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'quotes:view:any', negated: true },
						],
					},
					boundedIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'quotes:view:any', negated: true },
						],
					},
				},
			},
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [
			'quoteKeywords',
			'quoteSalesRep',
			'quoteDateRange',
			'quoteClient',
			'quoteStatus',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/quotes/:id',
			objectLabel: 'Quote',
			objectTable: 'order',
			selectedFields: [{ fieldName: '_id', fieldLabel: 'ID' }],
		},
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
			],
		},
	],
	showResetButton: true,
	tableSorts: [
		{
			id: 'date-asc',
			name: 'Date (Earliest First)',
			fields: [
				{ fieldName: 'createdDate', objectTable: 'order', direction: 'ASC' },
			],
		},
		{
			id: 'date-desc',
			name: 'Date (Latest First)',
			fields: [
				{ fieldName: 'createdDate', objectTable: 'order', direction: 'DESC' },
			],
		},
		{
			id: 'client-name-asc',
			name: 'Client Name (A-Z)',
			fields: [
				{ fieldName: 'client_name', objectTable: 'order', direction: 'ASC' },
			],
		},
		{
			id: 'client-name-desc',
			name: 'Client Name (Z-A)',
			fields: [
				{ fieldName: 'client_name', objectTable: 'order', direction: 'DESC' },
			],
		},
		{
			id: 'total-asc',
			name: 'Total (Low to High)',
			fields: [{ fieldName: 'total', objectTable: 'order', direction: 'ASC' }],
		},
		{
			id: 'total-desc',
			name: 'Total (High to Low)',
			fields: [{ fieldName: 'total', objectTable: 'order', direction: 'DESC' }],
		},
	],
};
